import I18n from 'I18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { NewChatIcon } from './NewChatIcon';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { AcademyAssistantIcon } from './AcademyAssistantIcon';
import { AILoader } from 'ai-components-ui-library/components/loading/AILoader';
import { ChatTextInput } from 'academy-common-lib/features/search/components/ChatTextInput';
import { useTypewriter } from '../hooks/useTypewriter';
import { useDynamicSections, stripUnmatchedSectionTokens } from '../hooks/useDynamicSections';
import { MarkdownText } from './MarkdownText';
import { DynamicSectionRenderer } from './DynamicSectionRenderer';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function EmptyState() {
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    alignItems: "center",
    gap: "200",
    children: [/*#__PURE__*/_jsx(AcademyAssistantIcon, {
      size: 68
    }), /*#__PURE__*/_jsx(Text, {
      typography: "heading-500",
      textAlign: "center",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academy-ai-assistant.chat.emptyState"
      })
    })]
  });
}
function UserMessage({
  content
}) {
  return /*#__PURE__*/_jsx(Flex, {
    justifyContent: "end",
    inlineSize: "100%",
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      alignItems: "end",
      maxInlineSize: "80%",
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        paddingBlock: "300",
        paddingInline: "400",
        backgroundColor: "fill-surface-recessed",
        borderStyle: "solid",
        borderWidth: "100",
        borderColor: "border-container-default",
        borderStartStartRadius: "300",
        borderStartEndRadius: "0",
        borderEndStartRadius: "300",
        borderEndEndRadius: "300",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          children: content
        })
      })
    })
  });
}
function AssistantMessage({
  message,
  animate,
  stoppedByUser,
  isLast,
  onAnimatingChange
}) {
  const {
    content,
    dynamicSectionContainers,
    reasoningDurationSeconds
  } = message;
  const strippedContent = stripUnmatchedSectionTokens(content, dynamicSectionContainers);
  const [isAnimating, setIsAnimating] = useState(animate);
  const [prevAnimate, setPrevAnimate] = useState(animate);
  if (prevAnimate !== animate) {
    setPrevAnimate(animate);
    if (animate) {
      setIsAnimating(true);
    }
  }
  const {
    displayedText,
    isComplete
  } = useTypewriter(isAnimating ? strippedContent : '');
  if (isAnimating && stoppedByUser && isLast) {
    setIsAnimating(false);
  } else if (!animate && isAnimating && (isComplete || !content)) {
    setIsAnimating(false);
  }
  const onAnimatingChangeRef = useRef(onAnimatingChange);
  onAnimatingChangeRef.current = onAnimatingChange;
  useEffect(() => {
    var _onAnimatingChangeRef;
    (_onAnimatingChangeRef = onAnimatingChangeRef.current) === null || _onAnimatingChangeRef === void 0 || _onAnimatingChangeRef.call(onAnimatingChangeRef, isAnimating);
  }, [isAnimating]);
  const visibleContent = isAnimating ? displayedText : strippedContent;
  const segments = useDynamicSections(visibleContent, dynamicSectionContainers);
  const showReasoning = typeof reasoningDurationSeconds === 'number' && reasoningDurationSeconds > 0;
  return /*#__PURE__*/_jsxs(View, {
    maxInlineSize: "100%",
    inlineSize: "100%",
    children: [showReasoning ? /*#__PURE__*/_jsx(Text, {
      typography: "detail-100",
      color: "text-core-subtle",
      paddingBlockEnd: "100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academy-ai-assistant.chat.reasonedForSeconds",
        options: {
          count: reasoningDurationSeconds
        }
      })
    }) : null, visibleContent ? /*#__PURE__*/_jsx(_Fragment, {
      children: segments.map((segment, index) => segment.type === 'text' ? /*#__PURE__*/_jsx(MarkdownText, {
        text: segment.content
      }, `text-${index}`) : /*#__PURE__*/_jsx(DynamicSectionRenderer, {
        container: segment.container
      }, `section-${segment.container.id}`))
    }) : isAnimating ? /*#__PURE__*/_jsx(AILoader, {}) : null]
  });
}
function ErrorMessage({
  error
}) {
  return /*#__PURE__*/_jsx(Flex, {
    justifyContent: "center",
    children: /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      color: "text-alert-default",
      children: error
    })
  });
}
export function AcademyAIAssistantChat({
  messages,
  isStreaming,
  error,
  onSendMessage,
  onStopStream,
  onClose,
  onNewChat
}) {
  const [inputValue, setInputValue] = useState('');
  const [stoppedByUser, setStoppedByUser] = useState(false);
  const [pendingSend, setPendingSend] = useState(null);
  const [lastMessageAnimating, setLastMessageAnimating] = useState(false);
  const scrollContainerRef = useRef(null);
  const prevMessageCountRef = useRef(messages.length);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (messages.length > prevMessageCountRef.current && container) {
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);
  const handleInputChange = useCallback(e => {
    setInputValue(e.target.value);
  }, []);
  useEffect(() => {
    if (pendingSend && !isStreaming) {
      setStoppedByUser(false);
      onSendMessage(pendingSend);
      setPendingSend(null);
    }
  }, [pendingSend, isStreaming, onSendMessage]);
  const handleStop = useCallback(() => {
    setStoppedByUser(true);
    onStopStream();
  }, [onStopStream]);
  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (isStreaming) {
      setStoppedByUser(true);
      onStopStream();
      setPendingSend(trimmed);
      setInputValue('');
      return;
    }
    setStoppedByUser(false);
    onSendMessage(trimmed);
    setInputValue('');
  }, [inputValue, isStreaming, onStopStream, onSendMessage]);
  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);
  const chatInput = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      boxShadow: "300",
      borderRadius: "300",
      children: /*#__PURE__*/_jsx(ChatTextInput, {
        placeholder: I18n.text('academy-ai-assistant.chat.inputPlaceholder'),
        value: inputValue,
        onChatInputChanged: handleInputChange,
        onKeyDown: handleKeyDown,
        onSend: handleSend,
        leadingIcon: null,
        onStop: isStreaming || lastMessageAnimating ? handleStop : undefined
      })
    }), /*#__PURE__*/_jsx(Text, {
      typography: "detail-100",
      color: "text-core-disabled",
      textAlign: "center",
      display: "block",
      paddingBlockStart: "300",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "academy-ai-assistant.chat.disclaimer_jsx",
        elements: {
          Link: ({
            children
          }) => /*#__PURE__*/_jsx("a", {
            href: "https://legal.hubspot.com/product-specific-terms#AI-prod",
            target: "_blank",
            rel: "noopener noreferrer",
            style: {
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 'inherit'
            },
            children: children
          })
        }
      })
    })]
  });
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    blockSize: "100%",
    backgroundColor: "fill-surface-default",
    children: [/*#__PURE__*/_jsx(View, {
      paddingBlock: "200",
      paddingInline: "300",
      borderBlockEndWidth: "100",
      borderColor: "border-core-default",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "500",
          children: [/*#__PURE__*/_jsx(Button, {
            use: "unstyled",
            size: "extra-small",
            onClick: onClose,
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              gap: "25",
              children: [/*#__PURE__*/_jsx(LeftIcon, {
                size: "100"
              }), /*#__PURE__*/_jsx(Text, {
                typography: "heading-25",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "academy-ai-assistant.chat.back"
                })
              })]
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "200",
            children: [/*#__PURE__*/_jsx(AcademyAssistantIcon, {
              size: 28
            }), /*#__PURE__*/_jsx(Text, {
              typography: "heading-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "academy-ai-assistant.chat.title"
              })
            })]
          })]
        }), messages.length > 0 ? /*#__PURE__*/_jsx(Tooltip, {
          placement: "bottom left",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academy-ai-assistant.chat.newChatTooltip"
          }),
          children: /*#__PURE__*/_jsx(View, {
            as: "span",
            display: "inline-block",
            padding: "100",
            children: /*#__PURE__*/_jsx(IconButton, {
              "aria-label": I18n.text('academy-ai-assistant.chat.newChat'),
              onClick: onNewChat,
              shape: "circle",
              use: "transparent",
              size: "sm",
              children: /*#__PURE__*/_jsx(NewChatIcon, {
                size: 28
              })
            })
          })
        }) : null]
      })
    }), !messages.length ? /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      padding: "300",
      children: [/*#__PURE__*/_jsx(EmptyState, {}), /*#__PURE__*/_jsx(View, {
        maxInlineSize: "800px",
        inlineSize: "100%",
        paddingBlockStart: "400",
        children: chatInput
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        ref: scrollContainerRef,
        flexGrow: 1,
        overflowY: "auto",
        padding: "300",
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          maxInlineSize: "800px",
          inlineSize: "100%",
          marginInline: "auto",
          children: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            gap: "600",
            children: [messages.map((msg, index) => msg.role === 'user' ? /*#__PURE__*/_jsx(UserMessage, {
              content: msg.content
            }, msg.timestamp) : /*#__PURE__*/_jsx(AssistantMessage, {
              message: msg,
              animate: isStreaming && index === messages.length - 1,
              stoppedByUser: stoppedByUser,
              isLast: index === messages.length - 1,
              onAnimatingChange: index === messages.length - 1 ? setLastMessageAnimating : undefined
            }, msg.timestamp)), error && /*#__PURE__*/_jsx(ErrorMessage, {
              error: error
            }), /*#__PURE__*/_jsx(View, {
              minBlockSize: "40px",
              flexShrink: 0
            })]
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        padding: "300",
        borderBlockStartWidth: "100",
        borderColor: "border-core-default",
        children: /*#__PURE__*/_jsx(View, {
          maxInlineSize: "800px",
          inlineSize: "100%",
          marginInline: "auto",
          children: chatInput
        })
      })]
    })]
  });
}